import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { Tooltip, Box, Alert, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { DataGrid } from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { JobRowData } from '@/api/definition'
import {
  fetchPaginatedJobs,
  postCancelJobs,
  postBulkRetryJobs,
  type FOTAJobRetryResponse,
} from '@/api/fota'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import type { FOTAJobListView } from '@/pages/fota/fota'
import {
  ConfirmErrorState,
  ConfirmTimeoutState,
  DownloadErrorState,
  DownloadTimeoutState,
  InstallErrorState,
  UpdateTimeoutState,
} from '@/pages/fota/jobState'
import {
  DateTime,
  FotaJobStateFormatter,
  IsOnline,
  Uid,
} from '@/pages/utils/formatter'
import { useFotaFailedFilters } from '@/hooks/useTransalationForFilter'
import { useFilters } from '@/hooks/useFilters'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import { FilterDrawer } from '@/components/FilterDrawer'
import { SelectedFilters } from '@/models/filters'
import { useTableUrlState } from '@/hooks/useTableUrlState'

// Define error states as a constant to avoid repetition
const ERROR_STATES = [
  DownloadTimeoutState,
  UpdateTimeoutState,
  DownloadErrorState,
  InstallErrorState,
  ConfirmErrorState,
  ConfirmTimeoutState,
]

interface Props {
  autoRefresh?: boolean
  enableFilters?: boolean
  enableSearch?: boolean
}

// RetryAlert component to show check results
const RetryAlert = ({ results }: { results: FOTAJobRetryResponse[] }) => {
  const { t } = useTranslation(['di', 'app'])
  const passed = results.filter((i) => i.successful).length
  const failed = results.filter((i) => !i.successful).length
  if (passed === 0 && failed === 0) return null
  return (
    <Alert severity={failed > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
      {passed > 0 && t('di:fota.fotaCheckPassedMessage', { count: passed })}
      {failed > 0 && t('di:fota.fotaCheckFailedMessage', { count: failed })}
    </Alert>
  )
}

// WarningTooltip component for failed retries
const WarningTooltip = ({ row }: { row: FOTAJobRetryResponse }) => {
  const { t } = useTranslation(['di', 'app'])
  const translationKey = `device.fotaJobs.errors.${row.failureReason}`
  const translatedFailureReason = t(translationKey, row.failureReason ?? '')
  return (
    <Box>
      {row.failureReason ? (
        <Box>{translatedFailureReason}</Box>
      ) : (
        <Box sx={{ pb: 1 }}>{t('device.fotaJobs.retryFailedByCheck')}</Box>
      )}
    </Box>
  )
}

export const ErrorCodeTooltip = ({ row }: { row: FOTAJobListView }) => {
  const [t] = useTranslation(['di', 'app'])
  const getErrorMessage = (errorCode: string): string => {
    const translationKey = `device.fotaJobs.errors.${errorCode}`
    const translatedMessage = t(translationKey)
    // If the translation key does not exist, it will return the key itself.
    // So we need to handle such cases to return a default error message.
    if (translatedMessage === translationKey) {
      return t('device.fotaJobs.errors.UNKNOWN')
    }
    return translatedMessage
  }
  const isErrorState =
    row.state === DownloadErrorState ||
    row.state === InstallErrorState ||
    row.state === ConfirmErrorState
  if (!isErrorState) {
    return null
  }
  return (
    <Typography style={{ paddingBottom: 5 }}>
      {row.error_code
        ? getErrorMessage(row.error_code)
        : 'Failed to retrieve Error code. Check logs'}
    </Typography>
  )
}

const ListErrorJobs: React.FC<Props> = ({
  autoRefresh = false,
  enableFilters = true,
  enableSearch = true,
}) => {
  const { t } = useTranslation(['di', 'app', 'device', 'common'])
  const filterConfig = useFotaFailedFilters()
  const { enqueueSnackbar } = useSnackbar()

  const { urlState, updateUrlState } = useTableUrlState()

  const localFilterHooks = useFilters()

  const [data, setData] = useState<JobRowData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [rowCount, setRowCount] = useState<number>(0)
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false)
  const [openRetryDialog, setOpenRetryDialog] = useState<boolean>(false)
  const [selectedJobs, setSelectedJobs] = useState<number[]>([])
  const [retryResults, setRetryResults] = useState<FOTAJobRetryResponse[]>([])

  // Local search state
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout>()

  // Refs for managing state
  const mountedRef = useRef(false)
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Use URL state for these values
  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel
  const selectedFilters = urlState.filters

  // Calculate which states to query based on filters
  const getStatesToQuery = useCallback(
    (currentSelectedFilters: SelectedFilters): string[] => {
      if (!enableFilters) {
        return ERROR_STATES
      }
      const selectedStates = Object.values(currentSelectedFilters).flatMap(
        (g) => Object.keys(g),
      )
      if (selectedStates.length === 0) {
        return ERROR_STATES
      }
      return ERROR_STATES.filter((state) => selectedStates.includes(state))
    },
    [enableFilters],
  )

  const loadData = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSortModel: GridSortModel,
      currentSearchTerm: string,
      currentSelectedFilters: SelectedFilters,
      isAutoRefresh = false,
    ) => {
      if (!mountedRef.current) return

      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      if (!isAutoRefresh) setLoading(true)

      try {
        const hasSortModel = currentSortModel.length > 0
        let sortParam = 'updated_at'
        if (hasSortModel) {
          sortParam =
            currentSortModel[0].field === 'state'
              ? 'State'
              : currentSortModel[0].field
        }
        const sortDirection = hasSortModel ? currentSortModel[0].sort : 'desc'
        const statesToQuery = getStatesToQuery(currentSelectedFilters)

        const { data: jobData, totalRows } = await fetchPaginatedJobs({
          page: currentPage,
          pageSize: currentPageSize,
          sortBy: sortParam,
          sortDir: sortDirection,
          searchTerm: currentSearchTerm.trim(),
          state: statesToQuery,
        })

        if (!jobData) throw new Error('No data received from server')

        const canUpdate =
          mountedRef.current && !abortControllerRef.current?.signal.aborted

        if (canUpdate) {
          setData(jobData)
          setRowCount(totalRows)
        }
      } catch (error) {
        const canShowError =
          mountedRef.current && !abortControllerRef.current?.signal.aborted
        if (canShowError) {
          console.error('Error fetching data:', error)
          enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
        }
      } finally {
        if (mountedRef.current && !isAutoRefresh) {
          setLoading(false)
        }
      }
    },
    [getStatesToQuery, enqueueSnackbar, t],
  )

  // Single effect that watches URL state
  useEffect(() => {
    mountedRef.current = true
    loadData(page, pageSize, sortModel, searchTerm, selectedFilters)

    return () => {
      mountedRef.current = false
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [page, pageSize, sortModel, searchTerm, selectedFilters, loadData])

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && mountedRef.current) {
      autoRefreshIntervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          loadData(page, pageSize, sortModel, searchTerm, selectedFilters, true)
        }
      }, 30000)

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current)
          autoRefreshIntervalRef.current = null
        }
      }
    }
  }, [
    autoRefresh,
    page,
    pageSize,
    sortModel,
    searchTerm,
    selectedFilters,
    loadData,
  ])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  // Event handlers - update URL
  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update (300ms)
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...selectedFilters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0,
        filters: newFilters,
      })
    },
    [selectedFilters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = selectedFilters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [selectedFilters, handleFilterChange],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: model,
      })
    },
    [updateUrlState],
  )

  const handleRetryJobs = useCallback(async () => {
    setOpenRetryDialog(false)
    setLoading(true)
    try {
      const result = await postBulkRetryJobs(selectedJobs)
      setRetryResults(result)
      const failCount = result.filter((job) => !job.successful).length
      const passCount = result.filter((job) => job.successful).length
      if (failCount > 0 && passCount > 0) {
        enqueueSnackbar(
          t('di:fota.fotaRetryJobsToastsMessages.partiallyRetried', {
            failCount: failCount.toString(),
            passCount: passCount.toString(),
          }),
          { variant: 'warning' },
        )
      } else if (failCount === 0 && passCount > 0) {
        enqueueSnackbar(t('di:fota.fotaRetryJobsToastsMessages.success'), {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(t('di:fota.fotaRetryJobsToastsMessages.failed'), {
          variant: 'error',
        })
      }
      // Refresh data after retry
      loadData(page, pageSize, sortModel, searchTerm, selectedFilters)
    } catch (error) {
      console.error('Error during job retry:', error)
      enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
    } finally {
      setLoading(false)
      setSelectedJobs([])
    }
  }, [
    selectedJobs,
    enqueueSnackbar,
    t,
    loadData,
    page,
    pageSize,
    sortModel,
    searchTerm,
    selectedFilters,
  ])

  const handleCancelJob = useCallback(async () => {
    setOpenCancelDialog(false)
    setLoading(true)
    try {
      const result = await postCancelJobs(selectedJobs.map(Number))
      const failCount = result.filter((i) => !i.successful).length
      const passCount = result.filter((i) => i.successful).length
      if (failCount > 0 && passCount > 0) {
        enqueueSnackbar(
          t('di:fota.fotaCancelJobsToastsMessages.partiallyCompleted', {
            failCount: failCount.toString(),
            passCount: passCount.toString(),
          }),
          { variant: 'warning' },
        )
      } else if (failCount === 0 && passCount > 0) {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.success'), {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.failed'), {
          variant: 'error',
        })
      }
      // Refresh data after cancel
      loadData(page, pageSize, sortModel, searchTerm, selectedFilters)
    } catch (error) {
      console.error('Error during job cancellation:', error)
      enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
    } finally {
      setLoading(false)
      setSelectedJobs([])
    }
  }, [
    selectedJobs,
    enqueueSnackbar,
    t,
    loadData,
    page,
    pageSize,
    sortModel,
    searchTerm,
    selectedFilters,
  ])

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
        renderCell: (params) => <Uid value={params.value} />,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'is_online',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 80,
        renderCell: (params) => <IsOnline value={params.value} />,
      },
      {
        field: 'created_by',
        headerName: t('di:fota.createdBy'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'version',
        headerName: t('di:fota.firmwareVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 80,
      },
      {
        field: 'state',
        headerName: t('di:fota.state'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 80,
        renderCell: (params) => {
          const retryWarning = retryResults.find((i) => i.id === params.row.id)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {retryWarning && !retryWarning.successful && (
                <Tooltip arrow title={<WarningTooltip row={retryWarning} />}>
                  <WarningIcon color="warning" fontSize="inherit" />
                </Tooltip>
              )}
              {(params.row.state === DownloadErrorState ||
                params.row.state === InstallErrorState ||
                params.row.state === ConfirmErrorState) &&
                params.row.error_code && (
                  <Tooltip arrow title={<ErrorCodeTooltip row={params.row} />}>
                    <ErrorIcon color="error" fontSize="inherit" />
                  </Tooltip>
                )}
              {<FotaJobStateFormatter state={params.value} />}
            </Box>
          )
        },
      },
      {
        field: 'updated_at',
        headerName: t('di:fota.lastChange'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        renderCell: (params) => <DateTime value={params.value} />,
      },
    ],
    [t, retryResults],
  )

  const shouldShowCustomToolbar = enableFilters || enableSearch

  const activeFilterCount = enableFilters
    ? Object.values(selectedFilters).reduce(
        (count, group) => count + Object.keys(group).length,
        0,
      )
    : 0

  return (
    <>
      {retryResults.length > 0 && <RetryAlert results={retryResults} />}

      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
        }}
      >
        <DataGrid
          rowHeight={45}
          rows={data}
          columns={columns}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          loading={loading}
          checkboxSelection
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={rowCount}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          onSortModelChange={handleSortModelChange}
          onRowSelectionModelChange={(ids) => setSelectedJobs(ids as number[])}
          isRowSelectable={(params) => params.row.is_online}
          slots={
            shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
          }
          slotProps={{
            toolbar: shouldShowCustomToolbar
              ? ({
                  onFilterClick: enableFilters
                    ? localFilterHooks.openDrawer
                    : undefined,
                  activeFilterCount: activeFilterCount,
                  enableFilters: enableFilters,
                  enableSearch: enableSearch,
                  selectedFilters: selectedFilters,
                  filterConfig: filterConfig,
                  onRemoveFilter: handleRemoveFilter,
                  searchValue: localSearchTerm,
                  onSearchChange: handleSearchChange,
                } as CustomToolbarProps)
              : undefined,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          localeText={{
            noRowsLabel: 'No error jobs found',
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {enableFilters && filterConfig && (
        <FilterDrawer
          open={localFilterHooks.isDrawerOpen}
          onClose={localFilterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
        />
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Tooltip title={t('device.fotaJobs.retry')}>
          <span>
            <Button
              variant="contained"
              onClick={() => setOpenRetryDialog(true)}
              disabled={!selectedJobs.length || loading}
              sx={{
                backgroundColor: '#009DE0',
                '&.Mui-disabled': {
                  opacity: 0.65,
                  backgroundColor: '#009DE0',
                  color: '#fff',
                },
              }}
            >
              {t('device.fotaJobs.retry')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t('device.fotaJobs.cancel')}>
          <span>
            <Button
              variant="contained"
              disabled={!selectedJobs.length || loading}
              onClick={() => setOpenCancelDialog(true)}
              sx={{
                backgroundColor: '#ff9800',
                '&.Mui-disabled': {
                  opacity: 0.65,
                  backgroundColor: '#ff9800',
                  color: '#fff',
                },
              }}
            >
              {t('device.fotaJobs.cancel')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ConfirmDialog
        open={openRetryDialog}
        onClose={() => setOpenRetryDialog(false)}
        onConfirm={handleRetryJobs}
        setOpen={setOpenRetryDialog}
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.retryConfirm')}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        onConfirm={handleCancelJob}
        setOpen={setOpenCancelDialog}
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmCancelFailedJobs')}
        </Typography>
      </ConfirmDialog>
    </>
  )
}

export default ListErrorJobs
